import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showTokenExpiredAlert, getConfig, authHeader, getCustId, isApiSuccess } from "utils";

// Plan Type dropdown: GET subscriber/commonList/generic/planGroupCust
// -> dataList [{ text: "Renew+OTT", value: "Renew+VAS" }, { text: "Volume Booster" }, { text: "Renew" }]
export const planGroupList = createAsyncThunk('recharge/planGroup', async ({ navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + 'subscriber/commonList/generic/planGroupCust',
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// GET subscriber/checkWalletBalanceUse -> data { isPartnerWalletUse, isAdminWalletUse, custPortalDynamicParamList }
export const walletBalanceUse = createAsyncThunk('recharge/walletBalanceUse', async ({ navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + 'subscriber/checkWalletBalanceUse',
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// Volume Booster only: GET subscriber/checkEligibilityAddon/{custId}
// -> data { isConvertToAddon, isValidActivePlan }; not eligible when isConvertToAddon && !isValidActivePlan
export const eligibilityAddon = createAsyncThunk('recharge/eligibilityAddon', async ({ navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `subscriber/checkEligibilityAddon/${getCustId()}`,
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// POST subscriber/plan/NewBypartner { partnerId, planGroup, serviceType: 1, validity: "1"|"3"|"6"|"12"|"other" } -> dataList
export const rechargePlanList = createAsyncThunk('recharge/planList', async ({ partnerId, planGroup, validity, navigate }) => {
    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'subscriber/plan/NewBypartner',
            data: {
                "partnerId": partnerId,
                "planGroup": planGroup,
                "serviceType": 1,
                "validity": validity,
            },
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// Buy popup payment methods: GET subscriber/getPartnerPGConfigByPartnerId/{partnerId}
// -> dataList [{ paymentGateWayId, paymentGateWayName, defaultpgflag: "Yes"|"No" }]
export const partnerPgConfig = createAsyncThunk('recharge/pgList', async ({ partnerId, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `subscriber/getPartnerPGConfigByPartnerId/${partnerId}`,
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// "Proceed to payment": POST subscriber/order/place — payload exactly as the portal builds it
export const placeOrder = createAsyncThunk('recharge/placeOrder', async ({ planId, pgId, planGroup, selectedVASIds }) => {
    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'subscriber/order/place',
            data: {
                "custId": getCustId(),
                "entityid": planId,
                "pgid": pgId,
                "ordertype": "plan",
                "finalamount": 0,
                "is_balance_used": true,
                "is_settled": false,
                "orderdesc": "dsa",
                "partnerId": 0,
                "taxamount": 0,
                "purchase_type": planGroup === "Renew+VAS" ? "Renew" : planGroup,
                "selectedVASIds": selectedVASIds || [],
            },
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// ---- Payment gateway flow (portal: PaymentMidpage + PaymentStatus) ----
const getApi = async (path) => {
    try {
        return await axios({ method: 'GET', url: getConfig.API_URL + path, headers: authHeader() })
    } catch (error) {
        return error.response
    }
}

// redirecttopayment/:id -> GET subscriber/order/ByTransId/{uniqueId}
// -> data { uniqueId, submitUrl, pgDetails: { ...gateway form fields }, paymentStatus, pgResStatus }
export const orderByTransId = createAsyncThunk('recharge/orderByTransId', async ({ id }) => getApi(`subscriber/order/ByTransId/${id}`))

// GET subscriber/order/checkOrder/{uniqueId} -> responseCode 200 => gateway form may be submitted
export const checkOrder = createAsyncThunk('recharge/checkOrder', async ({ id }) => getApi(`subscriber/order/checkOrder/${id}`))

// recharge/payment-status?txnid= -> GET subscriber/txnStatus/{txnid}
// -> data { transid, paymentstatus: "Pending" | "Successful" | "Failed", ... }
export const txnStatus = createAsyncThunk('recharge/txnStatus', async ({ txnid }) => getApi(`subscriber/txnStatus/${txnid}`))

const recharge = createSlice({
    name: 'recharge',
    initialState: {
        planGroup: [],
        walletUse: null,
        planList: [],
        pgList: [],
        loading: false,
        planLoading: false,
    },
    extraReducers: (builder) => {
        builder.addCase(planGroupList.fulfilled, (state, action) => {
            if (isApiSuccess(action?.payload)) {
                state.planGroup = action.payload.data.dataList || [];
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            }
        });

        builder.addCase(walletBalanceUse.fulfilled, (state, action) => {
            if (isApiSuccess(action?.payload)) {
                state.walletUse = action.payload.data.data;
            }
        });

        builder.addCase(rechargePlanList.pending, (state) => {
            state.planLoading = true;
        });
        builder.addCase(rechargePlanList.fulfilled, (state, action) => {
            state.planLoading = false;
            if (isApiSuccess(action?.payload)) {
                state.planList = action.payload.data.dataList || [];
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else {
                state.planList = []
            }
        });
        builder.addCase(rechargePlanList.rejected, (state) => {
            state.planLoading = false;
        });

        builder.addCase(partnerPgConfig.fulfilled, (state, action) => {
            state.pgList = isApiSuccess(action?.payload) ? (action.payload.data.dataList || []) : [];
        });
    }
})

export default recharge.reducer;
